import { Line, LineChart, ResponsiveContainer } from "recharts";
import type { HistoryRow } from "../../lib/networth-utils";

export type ClassKey = "LIQUIDE" | "ANLAGEN" | "IMMOBILIE" | "SONSTIGE" | "VERBINDLICHKEITEN";

interface Props {
  data: HistoryRow[];
  activeClass: ClassKey | null;
  onClassClick: (key: ClassKey) => void;
}

const CLASS_COLORS: Record<ClassKey, string> = {
  LIQUIDE: "#3b82f6",
  ANLAGEN: "#10b981",
  IMMOBILIE: "#f59e0b",
  SONSTIGE: "#a855f7",
  VERBINDLICHKEITEN: "#ef4444",
};

const CLASS_LABELS: Record<ClassKey, string> = {
  LIQUIDE: "Liquide",
  ANLAGEN: "Anlagen",
  IMMOBILIE: "Immobilie",
  SONSTIGE: "Sonstiges",
  VERBINDLICHKEITEN: "Verbindlichkeiten",
};

function formatEurCompact(cents: number): string {
  const eur = cents / 100;
  if (Math.abs(eur) >= 1000) return `${(eur / 1000).toFixed(1)}k €`;
  return `${eur.toFixed(0)} €`;
}

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

export function SparklinePanel({ data, activeClass, onClassClick }: Props) {
  if (data.length === 0) return null;

  const allKeys: ClassKey[] = ["LIQUIDE", "ANLAGEN", "IMMOBILIE", "SONSTIGE", "VERBINDLICHKEITEN"];

  // Verbindlichkeiten nur einblenden wenn in der Periode irgendwann > 0
  const visibleKeys = allKeys.filter((k) => {
    if (k === "VERBINDLICHKEITEN") {
      return data.some((row) => row.classes[k] > 0);
    }
    return true;
  });

  const isOddLast = visibleKeys.length % 2 === 1;

  return (
    <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
      {visibleKeys.map((k, idx) => {
        const start = data[0]?.classes[k] ?? 0;
        const end = data[data.length - 1]?.classes[k] ?? 0;
        const delta = end - start;
        const pct = start !== 0 ? (delta / start) * 100 : null;
        const trend = delta > 0 ? "↗" : delta < 0 ? "↘" : "→";
        const isActive = activeClass === k;
        const color = CLASS_COLORS[k];
        const isLastOddItem = isOddLast && idx === visibleKeys.length - 1;
        return (
          <button
            key={k}
            type="button"
            onClick={() => onClassClick(k)}
            className={`fv-card p-3 text-left transition-all hover:scale-[1.01] ${
              isLastOddItem ? "col-span-2 md:col-span-1" : ""
            }`}
            style={{
              border: isActive ? `2px solid ${color}` : "1px solid var(--border)",
            }}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold" style={{ color: "var(--muted)" }}>
                {CLASS_LABELS[k]}
              </span>
              <span className="w-3 h-1 rounded" style={{ background: color }} />
            </div>
            <div style={{ width: "100%", height: 40 }}>
              <ResponsiveContainer>
                <LineChart data={data}>
                  <Line
                    type="monotone"
                    dataKey={(row: HistoryRow) => row.classes[k]}
                    stroke={color}
                    strokeWidth={1.5}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="num font-semibold text-sm mt-1">{formatEur(end)}</div>
            <div className="text-xs flex items-center gap-1" style={{ color: "var(--muted)" }}>
              <span>{trend}</span>
              <span className="num">{formatEurCompact(delta)}</span>
              {pct !== null && (
                <span className="num">
                  ({pct >= 0 ? "+" : ""}
                  {pct.toFixed(1)}%)
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
